import React from "react";

// Kid-friendly text cleanup. The lesson content and the AI tutor sometimes
// emit markdown emphasis (**bold**, *italic*), stray backticks/#, and em/en
// dashes. Kids should never see raw symbols, and the parent asked for no
// asterisks or em-dashes anywhere. These helpers strip or render them nicely.

// Normalize dashes + drop code/heading markers (keeps * and ** for optional
// rich rendering by <RichText>).
function normalize(text) {
  return String(text || "")
    .replace(/`+/g, "")
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/\s*[-]\s*/g, ", ");  // normalize spaced dashes to comma
}

// Plain-text cleaner: removes ALL emphasis symbols. Use for lesson body,
// questions, options, and anything read aloud.
export function cleanText(text) {
  if (text == null) return text;
  let t = normalize(text);
  t = t.replace(/\*\*(.*?)\*\*/g, "$1"); // **bold**
  t = t.replace(/\*(.*?)\*/g, "$1");       // *italic*
  t = t.replace(/__(.*?)__/g, "$1").replace(/_(.*?)_/g, "$1");
  t = t.replace(/\*/g, "");                 // any stray asterisks
  return t;
}

// Deep-clean every string in a lesson object so no render path shows raw
// markdown. Returns a cleaned clone; leaves non-strings untouched.
export function deepCleanStrings(obj) {
  if (typeof obj === "string") return cleanText(obj);
  if (Array.isArray(obj)) return obj.map(deepCleanStrings);
  if (obj && typeof obj === "object") {
    const out = {};
    for (const k of Object.keys(obj)) out[k] = deepCleanStrings(obj[k]);
    return out;
  }
  return obj;
}

function renderInline(line, keyPrefix) {
  const parts = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={`${keyPrefix}-${i}`}>{p.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(p)) return <em key={`${keyPrefix}-${i}`}>{p.slice(1, -1)}</em>;
    return p.replace(/\*/g, "");
  });
}

// Renders text with **bold**/*italic* as real styling, dashes normalized, and
// line breaks preserved. Use in the tutor chat for a polished, readable reply.
export function RichText({ text }) {
  const src = normalize(text);
  const lines = src.split("\n");
  return lines.map((line, li) => {
    // Numbered steps (e.g. "1. Do this") render as tidy badge + text rows.
    const step = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
    if (step) {
      return (
        <div key={li} className="flex items-start gap-1.5 my-1">
          <span className="flex-shrink-0 w-4 h-4 rounded-full bg-emerald-500/30 text-emerald-100 text-[9px] font-extrabold flex items-center justify-center mt-[3px]">{step[1]}</span>
          <span className="flex-1">{renderInline(step[2], li)}</span>
        </div>
      );
    }
    if (!line.trim()) return <div key={li} className="h-1" />;
    return <div key={li}>{renderInline(line, li)}</div>;
  });
}
